import React, { useState, useEffect } from 'react';
import { FilterGroup, FilterCondition, FilterState } from './types/filters';
import { FilterGroupComponent } from './FilterGroup';
import { useLessonAction } from './tour/tourEvents';

interface FilterBarV2Props {
  columns: Array<{ key: string; label: string; type: string; category?: string }>;
  candidateColumns?: Array<{ key: string; label: string; type: string; category?: string }>;
  managerColumns?: Array<{ key: string; label: string; type: string; category?: string }>;
  customColumns?: Array<{ column_key: string; column_label: string; column_type: string }>;
  onApplyFilters: (filterState: FilterState) => void;
  currentDataType: string;
  projectId?: number;
  initialFilterState?: FilterState;
  totalRows: number;
  currentStartRow: number;
  currentLimit: number;
  onPaginationChange: (startRow: number, limit: number) => void;
  currentSort: { column: string; direction: 'asc' | 'desc' };
  onApplySort: (column: string, direction: 'asc' | 'desc') => void;
  // New props for additional buttons
  onOpenColumnSelector?: () => void;
  onOpenColumnOrder?: () => void;
  onToggleRadiusSearch?: () => void;
  radiusLocation?: string;
  radiusDistance?: number;
  // Clear filters
  onClearAllFilters?: () => void;
  hasAnyFilters?: boolean;
  // Job spec match mode - shows priority toggles
  showPriorityToggle?: boolean;
}

export const FilterBarV2: React.FC<FilterBarV2Props> = ({
  columns,
  candidateColumns,
  managerColumns,
  customColumns = [],
  onApplyFilters,
  currentDataType,
  projectId,
  initialFilterState,
  totalRows,
  currentStartRow,
  currentLimit,
  onPaginationChange,
  currentSort,
  onApplySort,
  onOpenColumnSelector,
  onOpenColumnOrder,
  onToggleRadiusSearch,
  radiusLocation,
  radiusDistance,
  onClearAllFilters,
  hasAnyFilters,
  showPriorityToggle = false
}) => {
  const [filterState, setFilterState] = useState<FilterState>(initialFilterState || {
    groups: [],
    employmentTypes: [],
  });
  
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  // Filters lesson: "Build the example for me" applies directly — close the panel like Apply does.
  useLessonAction('lesson-filters-example', () => setShowFilterMenu(false));
  // Employment type lesson: tick Contract + Freelance (the user presses Apply).
  useLessonAction('lesson-employment-example', () => {
    const picks = availableEmploymentTypes.filter((t) => /contract|freelance/i.test(t));
    setShowEmploymentMenu(true);
    setFilterState((prev) => ({ ...prev, employmentTypes: Array.from(new Set([...prev.employmentTypes, ...picks])) }));
  });
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [showRowMenu, setShowRowMenu] = useState(false);
  const [showEmploymentMenu, setShowEmploymentMenu] = useState(false);
  const [showRadiusMenu, setShowRadiusMenu] = useState(false);
  const [radiusType, setRadiusType] = useState<'candidate' | 'company'>('company');
  const [tempStartRow, setTempStartRow] = useState(currentStartRow);
  const [tempLimit, setTempLimit] = useState(currentLimit);
  
  const [availableEmploymentTypes, setAvailableEmploymentTypes] = useState<string[]>([]);

// Sync with initial filter state from AI
  useEffect(() => {
    if (initialFilterState) {
      setFilterState(initialFilterState);
    }
  }, [initialFilterState]);

  // Fetch employment types
  useEffect(() => {
    const fetchEmploymentTypes = async () => {
      if (!projectId) return;
      
      try {
        // One small query for the distinct types — never the whole candidate list.
        const response = await fetch(`/api/candidates/${projectId}/employment-types`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setAvailableEmploymentTypes(Array.isArray(data.types) ? data.types : []);
        }
      } catch (error) {
        console.error('Error fetching employment types:', error);
      }
    };
    
    fetchEmploymentTypes();
  }, [projectId]);

const getAvailableColumns = (entityType: 'company' | 'candidate' | 'manager') => {
  let baseColumns = columns;
  if (entityType === 'candidate' && candidateColumns) {
    baseColumns = candidateColumns;
  } else if (entityType === 'manager' && managerColumns) {
    baseColumns = managerColumns;
  }
  
  // Custom / AI columns have no fixed entity — the same key can hold values on
  // companies.custom_data AND candidates.custom_data (e.g. a company attribute
  // copied down to candidates), so they're offered in every group. Which entity
  // a group filters is the user's explicit choice (Add Company/Candidate group),
  // preserved by normalizeGroupEntities below.
  const customCols = customColumns.map(col => ({
    key: col.column_key,
    label: col.column_label,
    type: col.column_type,
    isCustom: true
  }));

  return [...baseColumns, ...customCols];
};

  // Filter Group Management
  const addFilterGroup = (entityType: 'company' | 'candidate' | 'manager' = 'candidate') => {
    const newGroup: FilterGroup = {
      id: `group-${Date.now()}`,
      name: '',
      logic: 'OR',
      entityType,
      conditions: [
        {
          id: `cond-${Date.now()}`,
          column: '',
          operator: 'contains',
          value: '',
          entityType
        }
      ]
    };
    
    setFilterState(prev => ({
      ...prev,
      groups: [...prev.groups, newGroup]
    }));
  };

  const updateFilterGroup = (groupId: string, updates: Partial<FilterGroup>) => {
    setFilterState(prev => ({
      ...prev,
      groups: prev.groups.map(g => g.id === groupId ? { ...g, ...updates } : g)
    }));
  };

  const removeFilterGroup = (groupId: string) => {
    setFilterState(prev => ({
      ...prev,
      groups: prev.groups.filter(g => g.id !== groupId)
    }));
  };

  const addConditionToGroup = (groupId: string) => {
    const group = filterState.groups.find(g => g.id === groupId);
    if (!group) return;

    const newCondition: FilterCondition = {
      id: `cond-${Date.now()}`,
      column: '',
      operator: 'contains',
      value: '',
      entityType: group.entityType
    };

    setFilterState(prev => ({
      ...prev,
      groups: prev.groups.map(g => 
        g.id === groupId 
          ? { ...g, conditions: [...g.conditions, newCondition] }
          : g
      )
    }));
  };

  const updateCondition = (groupId: string, conditionId: string, updates: Partial<FilterCondition>) => {
    setFilterState(prev => ({
      ...prev,
      groups: prev.groups.map(g => 
        g.id === groupId
          ? {
              ...g,
              conditions: g.conditions.map(c => 
                c.id === conditionId ? { ...c, ...updates } : c
              )
            }
          : g
      )
    }));
  };

  const removeCondition = (groupId: string, conditionId: string) => {
    setFilterState(prev => ({
      ...prev,
      groups: prev.groups.map(g => 
        g.id === groupId
          ? { ...g, conditions: g.conditions.filter(c => c.id !== conditionId) }
          : g
      ).filter(g => g.conditions.length > 0) // Remove empty groups
    }));
  };

  // Which entity a column actually belongs to. Prevents the trap where a filter
  // is built in a group labelled 'candidate' but targets a company column (or a
  // company custom column like an AI column) — the backend routes groups by
  // entityType, so a mislabelled group silently no-ops. Custom columns have no
  // stored entity, so they follow the active tab.
  const entityForColumn = (colKey: string): 'company' | 'candidate' | 'manager' | null => {
    const activeEntity: 'company' | 'candidate' | 'manager' =
      currentDataType === 'candidates' ? 'candidate' : currentDataType === 'managers' ? 'manager' : 'company';
    const inCompany = columns.some(c => c.key === colKey);
    const inCandidate = (candidateColumns || []).some(c => c.key === colKey);
    const inManager = (managerColumns || []).some(c => c.key === colKey);
    const isCustom = customColumns.some(c => c.column_key === colKey);
    // Custom/AI columns have no inherent entity — keep the group's chosen type
    // rather than forcing it to the active tab (which silently flipped a
    // Company group to Candidate when a custom column was picked).
    if (isCustom) return null;
    // Prefer the active tab when a bare column name exists in more than one set.
    if (activeEntity === 'company' && inCompany) return 'company';
    if (activeEntity === 'candidate' && inCandidate) return 'candidate';
    if (activeEntity === 'manager' && inManager) return 'manager';
    if (inCompany) return 'company';
    if (inCandidate) return 'candidate';
    if (inManager) return 'manager';
    return activeEntity;
  };

  // Stamp each group's entityType from the entity its first column targets, so
  // the filter routes correctly regardless of which "Add … Filter Group" button
  // built it.
  const normalizeGroupEntities = (state: FilterState): FilterState => ({
    ...state,
    groups: (state.groups || []).map(g => {
      let et = g.entityType;
      const firstCol = (g.conditions || []).find(c => c.column);
      // Only re-stamp when the column definitively belongs to another entity
      // (a standard column). Custom columns return null → keep the user's choice.
      if (firstCol) {
        const derived = entityForColumn(firstCol.column);
        if (derived) et = derived;
      }
      return { ...g, entityType: et, conditions: (g.conditions || []).map(c => ({ ...c, entityType: et })) };
    }),
  });

  const applyFilters = () => {
    onApplyFilters(normalizeGroupEntities(filterState));
    setShowFilterMenu(false);
  };

  const clearAllFilters = () => {
    const emptyState: FilterState = {
      groups: [],
      employmentTypes: [],
    };
    setFilterState(emptyState);
    onApplyFilters(emptyState);
  };

  const applySort = (column: string) => {
    const newDirection = currentSort.column === column && currentSort.direction === 'asc' ? 'desc' : 'asc';
    onApplySort(column, newDirection);
    setShowSortMenu(false);
  };

  const applyPagination = () => {
    const startRow = Number.isFinite(tempStartRow) && tempStartRow >= 0 ? tempStartRow : 0;
    const limit = Number.isFinite(tempLimit) && tempLimit > 0 ? tempLimit : totalRows;
    onPaginationChange(startRow, limit);
    setShowRowMenu(false);
  };

  const totalActiveFilters = filterState.groups.length + 
    (filterState.employmentTypes.length > 0 ? 1 : 0);

  return (
    <div className="border-b bg-white sticky top-0 z-10">
      <div className="flex items-center gap-2 p-2 flex-wrap">
        
        {/* Row Controls */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowRowMenu(!showRowMenu)}
            className="px-3 py-1.5 text-xs border border-gray-300 rounded hover:bg-gray-50 flex items-center gap-1"
          >
            <span>📊</span>
            Rows: {currentStartRow + 1}-{Math.min(currentStartRow + currentLimit, totalRows)} of {totalRows}
          </button>
          {showRowMenu && (
            <div className="absolute top-full mt-1 left-0 bg-white border border-gray-300 rounded-lg shadow-lg w-64 z-30">
              <div className="p-3">
                <div className="mb-3">
                  <label className="block text-xs font-medium mb-1">Start from row</label>
                  <input
                    type="number"
                    value={Number.isFinite(tempStartRow) ? tempStartRow : ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === '') { setTempStartRow(NaN); return; }
                      const n = parseInt(v, 10);
                      setTempStartRow(Number.isFinite(n) ? Math.max(0, n) : NaN);
                    }}
                    min="0"
                    max={totalRows - 1}
                    className="w-full px-2 py-1 text-xs border rounded"
                  />
                </div>
                <div className="mb-3">
                  <label className="block text-xs font-medium mb-1">Show rows</label>
                  <input
                    type="number"
                    value={Number.isFinite(tempLimit) && tempLimit > 0 ? tempLimit : ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === '') { setTempLimit(NaN); return; }
                      const n = parseInt(v, 10);
                      setTempLimit(Number.isFinite(n) ? Math.max(1, n) : NaN);
                    }}
                    placeholder="All rows"
                    min="1"
                    className="w-full px-2 py-1 text-xs border rounded"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={applyPagination}
                    className="flex-1 px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Apply
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowRowMenu(false)}
                    className="flex-1 px-3 py-1.5 text-xs border rounded hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Employment Type Filter - show on candidates and companies tabs */}
        {(currentDataType === 'candidates' || currentDataType === 'companies') && (
          <div className="relative">
            <button
              type="button"
              data-tour="employment-type-button"
              onClick={() => setShowEmploymentMenu(!showEmploymentMenu)}
              className={`px-3 py-1.5 text-xs border rounded hover:bg-gray-50 flex items-center gap-1 ${
                filterState.employmentTypes.length > 0 ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-300'
              }`}
            >
              <span>💼</span>
              {filterState.employmentTypes.length > 0
                ? `${filterState.employmentTypes.length} employment type${filterState.employmentTypes.length > 1 ? 's' : ''}`
                : 'Employment Type'}
            </button>
            {showEmploymentMenu && (
              <div data-tour="employment-type-panel" className="absolute top-full mt-1 left-0 bg-white border border-gray-300 rounded-lg shadow-lg w-56 z-30">
                <div className="p-2 border-b bg-gray-50 flex justify-between items-center">
                  <span className="text-xs font-medium">Employment Type</span>
                  <button
                    type="button"
                    onClick={() => setShowEmploymentMenu(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                </div>
                <div className="p-2 max-h-64 overflow-y-auto">
                  {/* Is Empty option */}
                  <label className="flex items-center px-2 py-1.5 hover:bg-gray-50 rounded cursor-pointer border-b border-gray-100 mb-1 pb-2">
                    <input
                      type="checkbox"
                      checked={filterState.employmentTypes.includes('__empty__')}
                      onChange={() => {
                        setFilterState(prev => ({
                          ...prev,
                          employmentTypes: prev.employmentTypes.includes('__empty__')
                            ? prev.employmentTypes.filter(t => t !== '__empty__')
                            : [...prev.employmentTypes, '__empty__']
                        }));
                      }}
                      className="mr-2"
                    />
                    <span className="text-xs italic text-gray-500">Is empty</span>
                  </label>
                  {availableEmploymentTypes.map(type => (
                    <label key={type} className="flex items-center px-2 py-1.5 hover:bg-gray-50 rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filterState.employmentTypes.includes(type)}
                        onChange={() => {
                          setFilterState(prev => ({
                            ...prev,
                            employmentTypes: prev.employmentTypes.includes(type)
                              ? prev.employmentTypes.filter(t => t !== type)
                              : [...prev.employmentTypes, type]
                          }));
                        }}
                        className="mr-2"
                      />
                      <span className="text-xs">{type}</span>
                    </label>
                  ))}
                </div>
                <div className="p-2 border-t">
                  <button
                    type="button"
                    data-tour="employment-type-apply"
                    onClick={() => {
                      onApplyFilters(normalizeGroupEntities(filterState));
                      setShowEmploymentMenu(false);
                    }}
                    className="w-full px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Filter Groups Button */}
        <div className="relative">
          <button
            type="button"
            data-tour="filters-button"
            onClick={() => setShowFilterMenu(!showFilterMenu)}
            className={`px-3 py-1.5 text-xs border rounded hover:bg-gray-50 flex items-center gap-1 ${
              totalActiveFilters > 0 ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-300'
            }`}
          >
            <span>🔍</span>
            {totalActiveFilters > 0 ? `${totalActiveFilters} filter${totalActiveFilters > 1 ? 's' : ''}` : 'Filters'}
          </button>
          {showFilterMenu && (
<div data-tour="filter-groups-panel" className="absolute top-full mt-1 left-0 bg-white border border-gray-300 rounded-lg shadow-lg w-[700px] max-h-[75vh] overflow-y-auto z-30">
              <div className="p-3 border-b bg-gray-50 flex justify-between items-center sticky top-0">
                <span className="text-xs font-medium">Filter Groups</span>
                <div className="flex gap-2">
                  {totalActiveFilters > 0 && (
                    <button
                      type="button"
                      onClick={clearAllFilters}
                      className="text-xs text-red-600 hover:text-red-800"
                    >
                      Clear All
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setShowFilterMenu(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                </div>
              </div>
              
              <div className="p-4">
                {filterState.groups.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 text-sm">
                    No filter groups yet. Add one to get started!
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filterState.groups.map((group, index) => (
                      <div key={group.id}>
                        {index > 0 && (
                          <div className="text-center my-2">
                            <span className="text-sm font-bold px-3 py-1 rounded bg-gray-200 text-gray-700">
                              AND
                            </span>
                          </div>
                        )}
                        <FilterGroupComponent
                          group={group}
                          availableColumns={getAvailableColumns(group.entityType)}
                          onUpdateGroup={updateFilterGroup}
                          onRemoveGroup={removeFilterGroup}
                          onAddCondition={addConditionToGroup}
                          onUpdateCondition={updateCondition}
                          onRemoveCondition={removeCondition}
                          showPriorityToggle={showPriorityToggle}
                        />
                      </div>
                    ))}
                  </div>
                )}

                <div className="mt-4 flex gap-2">
                  {currentDataType === 'managers' ? (
                    <button
                      type="button"
                      onClick={() => addFilterGroup('manager')}
                      className="flex-1 px-3 py-2 text-xs border-2 border-dashed border-gray-300 rounded hover:border-purple-500 hover:bg-purple-50"
                    >
                      + Add Manager Filter Group
                    </button>
                  ) : currentDataType === 'companies' ? (
                    <>
                      <button
                        type="button"
                        data-tour="filters-add-candidate-group"
                        onClick={() => addFilterGroup('candidate')}
                        className="flex-1 px-3 py-2 text-xs border-2 border-dashed border-gray-300 rounded hover:border-green-500 hover:bg-green-50"
                      >
                        + Add Candidate Filter Group
                      </button>
                      <button
                        type="button"
                        onClick={() => addFilterGroup('company')}
                        className="flex-1 px-3 py-2 text-xs border-2 border-dashed border-gray-300 rounded hover:border-blue-500 hover:bg-blue-50"
                      >
                        + Add Company Filter Group
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        data-tour="filters-add-candidate-group"
                        onClick={() => addFilterGroup('candidate')}
                        className="flex-1 px-3 py-2 text-xs border-2 border-dashed border-gray-300 rounded hover:border-green-500 hover:bg-green-50"
                      >
                        + Add Candidate Filter Group
                      </button>
                      <button
                        type="button"
                        onClick={() => addFilterGroup('company')}
                        className="flex-1 px-3 py-2 text-xs border-2 border-dashed border-gray-300 rounded hover:border-blue-500 hover:bg-blue-50"
                      >
                        + Add Company Filter Group
                      </button>
                    </>
                  )}
                </div>

                <div className="mt-4 pt-4 border-t">
                  <button
                    type="button"
                    data-tour="filters-apply"
                    onClick={applyFilters}
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-medium"
                  >
                    Apply Filters
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Sort */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowSortMenu(!showSortMenu)}
            className="px-3 py-1.5 text-xs border border-gray-300 rounded hover:bg-gray-50 flex items-center gap-1"
          >
            <span>↕️</span>
            Sort: {currentSort.column} {currentSort.direction === 'asc' ? '↑' : '↓'}
          </button>
          {showSortMenu && (
            <div className="absolute top-full mt-1 left-0 bg-white border border-gray-300 rounded-lg shadow-lg w-64 max-h-96 overflow-y-auto z-30">
              <div className="p-2">
                {(currentDataType === 'candidate' && candidateColumns ? candidateColumns : columns).map(col => (
                  <button
                    key={col.key}
                    type="button"
                    onClick={() => applySort(col.key)}
                    className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 rounded"
                  >
                    {col.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Radius Search */}
        {onToggleRadiusSearch && (
          <button
            type="button"
            data-tour="radius-button"
            onClick={onToggleRadiusSearch}
            className={`px-3 py-1.5 text-xs border rounded hover:bg-gray-50 ${
              radiusLocation ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-300'
            }`}
          >
            {radiusLocation ? `${radiusDistance}mi radius: ${radiusLocation}` : 'Radius search'}
          </button>
        )}

        {/* Customise Columns */}
        {onOpenColumnSelector && (
          <button
            type="button"
            onClick={onOpenColumnSelector}
            className="px-3 py-1.5 text-xs border border-gray-300 rounded hover:bg-gray-50 flex items-center gap-1"
          >
            🔧 Customise Columns
          </button>
        )}

        {/* Columns */}
        {onOpenColumnOrder && (
          <button
            type="button"
            onClick={onOpenColumnOrder}
            className="px-3 py-1.5 text-xs border border-gray-300 rounded hover:bg-gray-50 flex items-center gap-1"
          >
            ⋮⋮ Columns
          </button>
        )}

        {/* Clear All Filters */}
        {hasAnyFilters && onClearAllFilters && (
          <button
            type="button"
            onClick={onClearAllFilters}
            className="px-3 py-1.5 text-xs border border-red-300 bg-red-50 text-red-700 rounded hover:bg-red-100 flex items-center gap-1 font-medium"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            Clear Filters
          </button>
        )}

      </div>
    </div>
  );
};
